import React, { useEffect, useRef } from 'react';
import { BarChart3, Calculator, Layout, Megaphone, Palette, Pencil, Server, Settings, Share2, ShoppingCart, Truck, Users } from 'lucide-react';
import { Button } from './ui/button';
import { siteContent } from '../content/siteContent';

const solutionIconMap = {
  layout: Layout,
  shoppingCart: ShoppingCart,
  users: Users,
  barChart3: BarChart3,
  calculator: Calculator,
  truck: Truck,
  server: Server,
  settings: Settings,
  palette: Palette,
  megaphone: Megaphone,
  share2: Share2,
  pencil: Pencil,
};

const Solutions = () => {
  const sectionRef = useRef(null);
  const { solutions } = siteContent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const renderSolutionCard = (solution, index) => {
    const Icon = solutionIconMap[solution.icon];
    return (
      <div
        key={index}
        className="fade-in-up group p-6 rounded-2xl bg-gradient-to-br from-[#D22527]/5 to-transparent border border-[#D22527]/20 hover:border-[#D22527]/50 transition-all duration-500 hover:scale-105 backdrop-blur-sm hover:shadow-xl hover:shadow-[#D22527]/20"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D22527] to-[#88171A] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500 group-hover:rotate-6">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-white group-hover:text-[#D22527] transition-colors duration-300">
          {solution.name}
        </h3>
      </div>
    );
  };

  return (
    <section id="solutions" ref={sectionRef} className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#88171A] rounded-full filter blur-3xl opacity-10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-32">
          <div className="text-center mb-16">
            <div className="fade-in-up inline-block px-4 py-2 rounded-full bg-[#D22527]/10 border border-[#D22527]/30 text-[#D22527] text-sm font-semibold mb-6">
              {solutions.tech.badge}
            </div>
            <h2 className="fade-in-up text-5xl md:text-6xl font-bold text-white mb-6">
              {solutions.tech.heading[0]}
              <br />
              <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
                {solutions.tech.heading[1]}
              </span>
            </h2>
            <p className="fade-in-up text-xl text-gray-400 max-w-3xl mx-auto">{solutions.tech.intro}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {solutions.tech.items.map(renderSolutionCard)}
          </div>

          <div className="fade-in-up text-center">
            <Button className="bg-gradient-to-r from-[#D22527] to-[#88171A] text-white hover:shadow-lg hover:shadow-[#D22527]/50 transition-all duration-300 hover:scale-105 px-8 py-6 text-lg border-0">
              {solutions.tech.cta}
            </Button>
          </div>
        </div>

        <div>
          <div className="text-center mb-16">
            <div className="fade-in-up inline-block px-4 py-2 rounded-full bg-[#D22527]/10 border border-[#D22527]/30 text-[#D22527] text-sm font-semibold mb-6">
              {solutions.creative.badge}
            </div>
            <h2 className="fade-in-up text-5xl md:text-6xl font-bold text-white mb-6">
              {solutions.creative.heading[0]}
              <br />
              <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
                {solutions.creative.heading[1]}
              </span>
            </h2>
            <p className="fade-in-up text-xl text-gray-400 max-w-3xl mx-auto">{solutions.creative.intro}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {solutions.creative.items.map(renderSolutionCard)}
          </div>

          <div className="fade-in-up text-center">
            <Button className="bg-gradient-to-r from-[#D22527] to-[#88171A] text-white hover:shadow-lg hover:shadow-[#D22527]/50 transition-all duration-300 hover:scale-105 px-8 py-6 text-lg border-0">
              {solutions.creative.cta}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;
